import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAssetRenewStore } from '@/stores';
import MoreActionMenu from '@/components/common/MoreActionMenu';
import { likeCommunityPost, unlikeCommunityPost, shareCommunityPost } from '@/service/api/community';

export interface PostCardProps {
  post: Api.Community.PostResponse;
  formatNumber: (n?: number) => string;
  formatTime: (iso?: string) => string;
  onShowToast?: (msg: string) => void;
  onEditPost?: (post: Api.Community.PostResponse) => void;
  onDeletePost?: (post: Api.Community.PostResponse) => void;
  onReportPost?: (post: Api.Community.PostResponse) => void;
}

export default function PostCard({
  post,
  formatNumber,
  formatTime,
  onShowToast,
  onEditPost,
  onDeletePost,
  onReportPost,
}: PostCardProps) {
  const { user, rawUser } = useAuth();
  const assetCache = useAssetRenewStore((state) => state.assetCache);
  const [liked, setLiked] = useState<boolean>(!!post.is_liked);
  const [localLikes, setLocalLikes] = useState<number>(post.like_count || 0);
  const [localShareCount, setLocalShareCount] = useState<number>(post.share_count || 0);
  const [saved, setSaved] = useState<boolean>(false);

  useEffect(() => {
    setLocalShareCount(post.share_count || 0);
  }, [post.share_count]);

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!post.id) return;

    if (liked) {
      setLiked(false);
      setLocalLikes((prev) => Math.max(0, prev - 1));
      unlikeCommunityPost(post.id).catch(() => { });
    } else {
      setLiked(true);
      setLocalLikes((prev) => prev + 1);
      likeCommunityPost(post.id).catch(() => { });
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!post.id) return;

    const title = post.title?.trim() || '社区动态';
    const shareUrl = `${window.location.origin}/community/${post.id}`;
    const textToCopy = `【${title}】${shareUrl}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      onShowToast?.('已复制分享链接到剪贴板');
    } catch (err) {
      console.error('复制到剪贴板失败:', err);
      onShowToast?.('复制失败，请手动复制链接');
    }

    try {
      const res = await shareCommunityPost(post.id, { share_channel: 'LINK' });
      if (res && res.share_count !== undefined) {
        setLocalShareCount(res.share_count);
      } else {
        setLocalShareCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error('记录动态分享接口失败:', err);
    }
  };

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSaved(!saved);
    onShowToast?.(saved ? '已取消收藏' : '已添加收藏');
  };

  const mediaAssets = post.media_assets || [];
  const validMedia = mediaAssets.filter((m) => Boolean(m.access_url || m.source_access_url || m.hls_manifest_url));
  const defaultAvatar = `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author_account_id || post.id || 'default'}`;

  const authorAvatarAssetId = post.author_avatar_asset_id || post.author_avatar?.id;

  const authorAvatarUrl =
    (authorAvatarAssetId && assetCache[authorAvatarAssetId]?.url) ||
    post.author_avatar_access_url ||
    post.author_avatar?.access_url ||
    defaultAvatar;

  const getMediaUrl = (m: Api.Community.MediaAssetResponse) => {
    const assetId = m.asset_id;
    if (assetId && assetCache[assetId]?.url) {
      return assetCache[assetId].url;
    }
    return m.access_url || m.source_access_url || m.hls_manifest_url || '';
  };


  const isVideoMedia = (m: Api.Community.MediaAssetResponse) =>
    m.media_type?.toUpperCase() === 'VIDEO';

  const defaultTopicName = post.topics?.[0]?.name || 'AI短剧';
  const currentAccountId = rawUser?.id || user?.id;
  const isPostOwner = Boolean(
    currentAccountId &&
    post.author_account_id &&
    String(currentAccountId) === String(post.author_account_id)
  );

  return (
    <Link
      to={`/community/${post.id}`}
      className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 hover:shadow-md transition-all cursor-pointer block group"
    >
      {/* 头部：作者信息 & 级别 & 动作按钮 */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            {authorAvatarUrl ? (
              <img
                src={authorAvatarUrl}
                alt={post.author_display_name || '作者头像'}
                className="w-10 h-10 rounded-full object-cover border border-slate-100"
                loading="lazy"
              />
            ) : (

              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white font-bold flex items-center justify-center text-base shadow-xs select-none border border-slate-600/20">
                {(post.author_display_name || '灵')?.trim().slice(0, 1).toUpperCase()}
              </div>
            )}
            <span className="w-2.5 h-2.5 bg-slate-400 rounded-full border-2 border-white absolute bottom-0 right-0"></span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-foreground-950 truncate group-hover:text-slate-900 transition-colors">
                {post.author_display_name || '灵感玩家'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5">
              <span>{formatTime(post.published_at || post.created_at) || '2 小时前'}</span>
              <span>·</span>
              <span>发布于 {defaultTopicName}</span>
            </div>
          </div>
        </div>

        {/* 右侧更多选项 */}
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <MoreActionMenu
            align="right"
            buttonClassName="p-1 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            icon={<i className="ri-more-fill text-lg"></i>}
            items={[
              ...(isPostOwner
                ? [
                  {
                    key: 'edit-post',
                    label: '编辑动态',
                    icon: 'ri-edit-line',
                    onClick: () => onEditPost?.(post),
                  },
                  {
                    key: 'delete-post',
                    label: '删除动态',
                    icon: 'ri-delete-bin-line',
                    danger: true,
                    onClick: () => onDeletePost?.(post),
                  },
                ]
                : []),
              {
                key: 'report-post',
                label: '举报动态',
                icon: 'ri-flag-line',
                danger: true,
                onClick: () => onReportPost?.(post),
              },
            ]}
          />
        </div>
      </div>

      {/* 标题 */}
      {post.title && (
        <h3 className="font-bold text-base md:text-lg text-foreground-950 leading-snug my-2.5 group-hover:text-slate-900 transition-colors">
          {post.title}
        </h3>
      )}

      {/* 摘要内容 */}
      {post.content && (
        <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-2">
          {post.content}
        </p>
      )}

      {/* 媒体展现 (单图或单视频：按高度展示，靠左对齐，完整不裁切) */}
      {validMedia.length === 1 && (
        <div className="flex justify-start mb-3">
          <div className="relative rounded-2xl overflow-hidden border border-gray-100 bg-gray-50/50 max-h-[320px] sm:max-h-[380px] md:max-h-[420px] max-w-full inline-flex items-center justify-start">
            {isVideoMedia(validMedia[0]) ? (
              validMedia[0].video_status === 'PROCESSING' || validMedia[0].video_status === 'TRANSCODING' ? (
                <div className="w-64 h-48 flex flex-col items-center justify-center bg-gray-900 text-white p-4 rounded-2xl">
                  <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-xs text-gray-300">视频转码处理中...</span>
                </div>
              ) : (
                <div className="relative max-h-[320px] sm:max-h-[380px] md:max-h-[420px] max-w-full group/video overflow-hidden rounded-2xl bg-black">
                  <video
                    src={getMediaUrl(validMedia[0])}
                    preload="metadata"
                    className="max-h-[320px] sm:max-h-[380px] md:max-h-[420px] w-auto max-w-full object-contain rounded-2xl bg-black block"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-black/40 text-white flex items-center justify-center text-xl backdrop-blur-sm shadow-md group-hover/video:scale-110 transition-transform">
                      <i className="ri-play-fill pl-0.5 text-white"></i>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <img
                src={getMediaUrl(validMedia[0])}
                alt={post.title || '媒体图片'}
                className="max-h-[320px] sm:max-h-[380px] md:max-h-[420px] w-auto max-w-full object-contain rounded-2xl block"
                loading="lazy"
              />
            )}
          </div>
        </div>
      )}

      {/* 多媒体图/视频展示 (按4列布局，最多展示3项，多于3项时第3张叠加+xx蒙层) */}
      {validMedia.length > 1 && (
        <div className="grid grid-cols-4 gap-2 mb-3 aspect-[16/6] md:aspect-[3/1]">
          {validMedia.slice(0, 3).map((mediaItem, idx) => {
            const isTwo = validMedia.length === 2;
            const spanClass = isTwo ? 'col-span-2' : idx === 2 ? 'col-span-2' : 'col-span-1';
            const isLastItem = idx === 2;
            const hasMore = validMedia.length > 3 && isLastItem;
            const extraCount = validMedia.length - 2;
            const url = getMediaUrl(mediaItem);
            const isVid = isVideoMedia(mediaItem);

            return (
              <div
                key={mediaItem.asset_id || idx}
                className={`relative rounded-xl overflow-hidden border border-gray-100 bg-gray-50 h-full ${spanClass}`}
              >
                {isVid ? (
                  <div className="relative w-full h-full bg-black group/vid">
                    <video src={url} preload="metadata" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center text-sm backdrop-blur-sm shadow">
                        <i className="ri-play-fill pl-0.5"></i>
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={url}
                    alt={post.title || `媒体图 ${idx + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}

                {/* 超过3张时，第3张显示 +xx 蒙层 */}
                {hasMore && (
                  <div className="absolute inset-0 bg-black/55 backdrop-blur-[1px] flex items-center justify-center text-white font-bold text-base md:text-lg shadow-inner">
                    +{extraCount}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 话题 Pill 标签行 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-wrap gap-2">
          {post.topics && post.topics.length > 0 ? (
            post.topics.map((topic) => (
              <span
                key={topic.id || topic.name}
                className="text-xs px-3 py-1 rounded-full bg-slate-100/90 hover:bg-slate-200/90 text-slate-600 hover:text-slate-900 font-medium border border-slate-200/50 transition-colors"
              >
                # {topic.name}
              </span>
            ))
          ) : (
            <>
              <span className="text-xs px-3 py-1 rounded-full bg-slate-100/90 hover:bg-slate-200/90 text-slate-600 hover:text-slate-900 font-medium border border-slate-200/50 transition-colors">
                # AI短剧
              </span>
              <span className="text-xs px-3 py-1 rounded-full bg-slate-100/90 hover:bg-slate-200/90 text-slate-600 hover:text-slate-900 font-medium border border-slate-200/50 transition-colors">
                # 爆款经验
              </span>
            </>
          )}
        </div>
      </div>

      {/* 底部 4 大功能 Action Bar (分享、点赞、评论、收藏) */}
      <div className="grid grid-cols-4 pt-3 border-t border-gray-100 text-slate-400">
        {/* 分享 */}
        <button
          onClick={handleShare}
          className="flex items-center justify-center gap-1.5 text-xs md:text-sm hover:text-slate-800 transition-colors cursor-pointer py-1"
          title="分享"
        >
          <i className="ri-share-forward-line text-base"></i>
          <span>{localShareCount > 0 ? formatNumber(localShareCount) : '分享'}</span>
        </button>

        {/* 点赞 */}
        <button
          onClick={handleLike}
          className={`flex items-center justify-center gap-1.5 text-xs md:text-sm transition-colors cursor-pointer py-1 ${liked ? 'text-rose-500 font-semibold' : 'hover:text-rose-500'
            }`}
        >
          <i className={`${liked ? 'ri-thumb-up-fill text-rose-500' : 'ri-thumb-up-line'} text-base`}></i>
          <span>{localLikes > 0 ? formatNumber(localLikes) : '点赞'}</span>
        </button>

        {/* 评论 */}
        <div className="flex items-center justify-center gap-1.5 text-xs md:text-sm hover:text-slate-800 transition-colors cursor-pointer py-1">
          <i className="ri-chat-3-line text-base"></i>
          <span>{post.comment_count ? formatNumber(post.comment_count) : '评论'}</span>
        </div>

        {/* 收藏 */}
        <button
          onClick={handleSave}
          className={`flex items-center justify-center gap-1.5 text-xs md:text-sm transition-colors cursor-pointer py-1 ${saved ? 'text-slate-800 font-semibold' : 'hover:text-slate-800'
            }`}
        >
          <i className={`${saved ? 'ri-star-fill text-amber-500' : 'ri-star-line'} text-base`}></i>
          <span>{saved ? '已收藏' : '收藏'}</span>
        </button>
      </div>

      {/* 最新评论 preview 展示区域 */}
      {post.comments && post.comments.length > 0 && (
        <div className="mt-3 pt-2.5 border-t border-gray-100 bg-gray-50/70 rounded-xl p-3 space-y-2 text-xs">
          {post.comments.slice(0, 2).map((comment, index) => {
            const authorName =
              comment.author?.display_name ||
              (comment as any).author_display_name ||
              (comment.author_account_id ? `用户 ${comment.author_account_id.slice(-4)}` : '社区用户');

            const avatarUrl =
              comment.author?.avatar?.access_url ||
              comment.author?.avatar_access_url ||
              (comment as any).author_avatar_access_url ||
              (comment.author?.avatar_asset_id && typeof comment.author.avatar_asset_id === 'string' && comment.author.avatar_asset_id.startsWith('http') ? comment.author.avatar_asset_id : '');

            const firstChar = authorName.trim().slice(0, 1).toUpperCase() || '用';

            return (
              <div key={comment.id || index} className="flex items-center gap-2">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={authorName}
                    className="w-5 h-5 rounded-full object-cover shrink-0 border border-gray-200/50"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 text-white font-bold flex items-center justify-center text-[10px] shrink-0 select-none shadow-2xs">
                    {firstChar}
                  </div>
                )}
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="font-semibold text-foreground-900 shrink-0">
                    {authorName}
                  </span>
                  <span className="text-gray-400 text-[11px] shrink-0">:</span>
                  <span className="text-gray-600 truncate flex-1">{comment.content}</span>
                </div>
              </div>
            );
          })}

          {(post.comment_count || 0) > post.comments.slice(0, 2).length && (
            <div className="text-[11px] text-slate-700 font-medium pt-0.5 flex items-center gap-1 group-hover:text-slate-900 group-hover:underline">
              <span>查看全部 {post.comment_count || post.comments.length} 条评论</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
          )}
        </div>
      )}
    </Link>
  );
}
